import {
  STRAPI_URL,
  flattenStrapiEntry,
  strapiAuthHeaders,
  parseErrorMessage,
  type AuthResponse,
  type StrapiUser,
} from './strapi';
import {
  strapiCreatePermissionPresetForUser,
  strapiUpdatePermissionPresetAccesses,
  type PermissionAccessCatalogItem,
  unwrapPermissionAccessCatalogItems,
} from './strapiPermissionPresets';

export type StrapiUserDetail = {
  id: string;
  numericId: number | null;
  username: string;
  email: string;
  blocked: boolean;
  confirmed?: boolean;
  /** Related `permission-preset` integer id when present (legacy numeric PK). */
  permissionPresetId: string | null;
  /** Strapi document id for `permission-preset` (Content API routes). */
  permissionPresetDocumentId: string | null;
  accesses: PermissionAccessCatalogItem[];
  /**
   * Related `user-type` integer PK for `/api/users` body (`user_type`).
   * Users-permissions persists integer FKs only — do not send Content-API `documentId`.
   */
  userTypeId: string | null;
  userTypeCode: string | null;
  userTypeName: string | null;
  /** Integer PKs of assigned `analis-type-data` rows. */
  analisTypeDataIds: string[];
  analisTypeDataTitles: string[];
};

export type StrapiManagedUserRow = {
  id: string;
  /** Content-API document id when Strapi exposes it (relations / `connect`). */
  documentId: string | null;
  numericId: number | null;
  username: string;
  email: string;
  blocked: boolean;
  confirmed?: boolean;
  createdAt?: string;
  userTypeCode: string | null;
  userTypeName: string | null;
};

type StrapiListMeta = {
  pagination?: { page?: number; pageSize?: number; pageCount?: number; total?: number };
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function relationRecords(v: unknown): Record<string, unknown>[] {
  if (Array.isArray(v)) return v.flatMap((item) => relationRecords(item));
  if (!v || typeof v !== 'object') return [];
  if ('data' in v) return relationRecords((v as { data: unknown }).data);
  return [v as Record<string, unknown>];
}

function parseListBody(
  res: Response,
  body: unknown,
): { rows: Record<string, unknown>[]; meta: StrapiListMeta } {
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  /** Some Strapi/plugin responses return a bare array. */
  if (Array.isArray(body)) {
    return { rows: body as Record<string, unknown>[], meta: {} };
  }
  const b = asRecord(body);
  if (!b) {
    return { rows: [], meta: {} };
  }
  if (Array.isArray(b.data)) {
    return { rows: b.data as Record<string, unknown>[], meta: (b.meta as StrapiListMeta) || {} };
  }
  /** Users & Permissions plugin often returns `{ roles: [...] }` (not `data`); query filters may be ignored. */
  if (Array.isArray(b.roles)) {
    return { rows: b.roles as Record<string, unknown>[], meta: {} };
  }
  return { rows: [], meta: {} };
}

function unwrapPopulateOneEntry(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== 'object') return null;
  if ('data' in (v as object)) {
    const d = (v as { data: unknown }).data;
    if (d && typeof d === 'object' && !Array.isArray(d)) return d as Record<string, unknown>;
    return null;
  }
  if (Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

function normalizeUserEntry(raw: Record<string, unknown>): StrapiManagedUserRow {
  const src = flattenStrapiEntry(raw);
  /**
   * `/api/users` often returns `{ id, ...attributes }`. `flattenStrapiEntry` spreads
   * `attributes` but does not copy top-level `id`, so read `raw.id` for the integer PK.
   */
  const rawId = raw.id ?? src.id;
  const numericId = rawId != null && Number.isFinite(Number(rawId)) ? Number(rawId) : null;
  /** Users plugin REST uses integer `id` for routes and relations; avoid `documentId` here. */
  let id = numericId != null ? String(numericId) : '';
  if (!id) {
    const u = String(src.username ?? '');
    const em = String(src.email ?? '');
    id = u || em ? `u:${u}:${em}` : 'u:unknown';
  }

  const utWrap = unwrapPopulateOneEntry(raw.user_type ?? src.user_type);
  let userTypeCode: string | null = null;
  let userTypeName: string | null = null;
  if (utWrap) {
    const uf = flattenStrapiEntry(utWrap);
    userTypeCode = String(uf.type_code ?? '') || null;
    userTypeName = String(uf.type_name ?? '') || null;
  }

  const documentId =
    raw.documentId != null
      ? String(raw.documentId)
      : src.documentId != null
        ? String(src.documentId)
        : null;

  return {
    id,
    documentId,
    numericId,
    username: String(src.username ?? ''),
    email: String(src.email ?? ''),
    blocked: Boolean(src.blocked),
    confirmed: src.confirmed != null ? Boolean(src.confirmed) : undefined,
    createdAt: (src.createdAt as string) ?? (src.created_at as string) ?? undefined,
    userTypeCode,
    userTypeName,
  };
}

export type UsersListFilters = {
  /** Legacy: substring on email only when `searchContains` is empty. */
  emailContains: string;
  /** When set, matches username OR email (case-insensitive); overrides `emailContains` for the text filter. */
  searchContains?: string;
  status: 'all' | 'active' | 'inactive';
  /** Filter by related `user_type.type_code` (e.g. `petugas_sampling`). */
  userTypeCode?: string;
};

export type UsersListResult = { rows: StrapiManagedUserRow[]; total: number };

export async function strapiListUsers(
  jwt: string,
  filters: UsersListFilters,
  pageSize = 500,
): Promise<UsersListResult> {
  const params = new URLSearchParams({
    'pagination[pageSize]': String(pageSize),
    'pagination[page]': '1',
    sort: 'blocked:asc,username:asc',
  });
  const q = (filters.searchContains ?? '').trim() || filters.emailContains.trim();
  if (q) {
    params.append('filters[$or][0][email][$containsi]', q);
    params.append('filters[$or][1][username][$containsi]', q);
  }
  if (filters.status === 'active') {
    params.append('filters[blocked][$eq]', 'false');
  } else if (filters.status === 'inactive') {
    params.append('filters[blocked][$eq]', 'true');
  }
  const userTypeCode = filters.userTypeCode?.trim();
  if (userTypeCode) {
    params.append('filters[user_type][type_code][$eq]', userTypeCode);
  }
  params.append('populate[user_type]', 'true');

  const res = await fetch(`${STRAPI_URL}/api/users?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows, meta } = parseListBody(res, body);
  const total = meta.pagination?.total ?? rows.length;
  const mapped = rows.map((r) => normalizeUserEntry(r));
  mapped.sort((a, b) => {
    if (a.blocked !== b.blocked) return a.blocked ? 1 : -1;
    return a.username.localeCompare(b.username, undefined, { sensitivity: 'base' });
  });
  return { rows: mapped, total };
}

/** Payload for Strapi `manyToOne` → `plugin::users-permissions.user` (e.g. quotation.assignee_user). */
export function strapiUserRelationConnect(user: StrapiManagedUserRow): number | { connect: string[] } {
  if (user.documentId) {
    return { connect: [user.documentId] };
  }
  if (user.numericId != null) {
    return user.numericId;
  }
  throw new Error('User tidak punya id yang valid untuk Strapi.');
}

function normalizeUserDetail(raw: Record<string, unknown>): StrapiUserDetail {
  const src = flattenStrapiEntry(raw);
  const rawId = raw.id ?? src.id;
  const numericId = rawId != null && Number.isFinite(Number(rawId)) ? Number(rawId) : null;
  const id = numericId != null ? String(numericId) : '';

  const presetWrap = unwrapPopulateOneEntry(raw.permission_preset_context ?? src.permission_preset_context);

  const utWrap = unwrapPopulateOneEntry(raw.user_type ?? src.user_type);
  let userTypeId: string | null = null;
  let userTypeCode: string | null = null;
  let userTypeName: string | null = null;
  if (utWrap) {
    const uf = flattenStrapiEntry(utWrap);
    const utRid = utWrap.id ?? uf.id;
    if (utRid != null && Number.isFinite(Number(utRid))) {
      userTypeId = String(Number(utRid));
    }
    userTypeCode = String(uf.type_code ?? '') || null;
    userTypeName = String(uf.type_name ?? '') || null;
  }

  const analisRows = relationRecords(raw.analis_type_data ?? src.analis_type_data);
  const analisTypeDataIds: string[] = [];
  const analisTypeDataTitles: string[] = [];
  for (const row of analisRows) {
    const af = flattenStrapiEntry(row);
    const analisRid = row.id ?? af.id;
    if (analisRid == null || !Number.isFinite(Number(analisRid))) continue;
    const id = String(Number(analisRid));
    if (analisTypeDataIds.includes(id)) continue;
    analisTypeDataIds.push(id);
    analisTypeDataTitles.push(String(af.title ?? '').trim());
  }

  let permissionPresetId: string | null = null;
  let permissionPresetDocumentId: string | null = null;
  let accesses: PermissionAccessCatalogItem[] = [];
  if (presetWrap) {
    const pf = flattenStrapiEntry(presetWrap);
    const presetRid = presetWrap.id ?? pf.id;
    if (presetRid != null && Number.isFinite(Number(presetRid))) {
      permissionPresetId = String(Number(presetRid));
    }
    const pDoc = presetWrap.documentId ?? pf.documentId;
    if (pDoc != null) {
      permissionPresetDocumentId = String(pDoc);
    }
    accesses = unwrapPermissionAccessCatalogItems(
      presetWrap.permission_accesses ?? pf.permission_accesses,
    );
  }

  return {
    id,
    numericId,
    username: String(src.username ?? ''),
    email: String(src.email ?? ''),
    blocked: Boolean(src.blocked),
    confirmed: src.confirmed != null ? Boolean(src.confirmed) : undefined,
    permissionPresetId,
    permissionPresetDocumentId,
    accesses,
    userTypeId,
    userTypeCode,
    userTypeName,
    analisTypeDataIds,
    analisTypeDataTitles,
  };
}

function parseFindOneUser(res: Response, body: unknown): StrapiUserDetail {
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  const b = asRecord(body);
  if (!b) throw new Error('Invalid user response');
  const entry = unwrapPopulateOneEntry(b.data) ?? unwrapPopulateOneEntry(b) ?? b;
  if (!entry || typeof entry !== 'object' || !('username' in entry)) {
    throw new Error('Invalid user response');
  }
  return normalizeUserDetail(entry);
}

export async function strapiFetchUserDetail(jwt: string, userNumericId: string): Promise<StrapiUserDetail> {
  /**
   * Nested `permission_accesses.permission_group` on `/api/users` is rejected by Strapi
   * (`Invalid key permission_group at permission_accesses.permission_group`). Populate accesses only;
   * the app loads group labels from the permission-access catalog where needed.
   */
  /** Avoid dotted `a.b.c` populate — it can deep-expand `permission_accesses.*` and hit the same invalid `permission_group` populate path. */
  const attempts = [
    new URLSearchParams({
      'populate[permission_preset_context][populate][0]': 'permission_accesses',
      'populate[user_type]': 'true',
      'populate[analis_type_data]': 'true',
    }),
  ];

  let lastMessage = '';
  for (const qs of attempts) {
    const res = await fetch(`${STRAPI_URL}/api/users/${encodeURIComponent(userNumericId)}?${qs}`, {
      headers: strapiAuthHeaders(jwt),
    });
    const body = (await res.json().catch(() => ({}))) as unknown;

    try {
      if (res.ok) {
        return parseFindOneUser(res, body);
      }
      lastMessage = parseErrorMessage(body, res.status);
    } catch (e) {
      lastMessage = e instanceof Error ? e.message : 'Invalid user payload';
    }
  }

  throw new Error(lastMessage || 'Unable to load user');
}

function parseUserTypeRelationNumericId(raw: string): number {
  const u = raw.trim();
  if (!u) throw new Error('User type is required.');
  const n = Number(u);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) {
    throw new Error('Invalid user type id.');
  }
  return n;
}

/** Integer PKs of `analis-type-data`. Empty array clears the relation. */
function parseAnalisTypeDataIds(raw: string[]): number[] {
  const ids: number[] = [];
  for (const item of raw) {
    const n = Number(String(item).trim());
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) {
      throw new Error('Invalid analis type id.');
    }
    if (!ids.includes(n)) ids.push(n);
  }
  return ids;
}

export async function strapiUpdateManagedUser(
  jwt: string,
  userNumericId: string,
  patch: {
    username?: string;
    blocked?: boolean;
    password?: string | null;
    /** Users-permissions `user_type`: integer PK of `user-types` row (not `documentId`). */
    userTypeId?: string;
    /** Integer PKs of `analis-type-data`. Empty array clears the relation. */
    analisTypeDataIds?: string[];
  },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.username !== undefined) {
    payload.username = patch.username.trim();
  }
  if (patch.blocked !== undefined) {
    payload.blocked = patch.blocked;
  }
  if (patch.password != null && String(patch.password).trim() !== '') {
    payload.password = patch.password;
  }
  if (patch.userTypeId !== undefined) {
    payload.user_type = parseUserTypeRelationNumericId(patch.userTypeId);
  }
  if (patch.analisTypeDataIds !== undefined) {
    payload.analis_type_data = parseAnalisTypeDataIds(patch.analisTypeDataIds);
  }

  const res = await fetch(`${STRAPI_URL}/api/users/${encodeURIComponent(userNumericId)}`, {
    method: 'PUT',
    headers: {
      ...strapiAuthHeaders(jwt),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
}

/** Link user ↔ preset when POST preset did not populate the inverse relation (users-permissions quirk). */
async function strapiLinkUserPermissionPreset(
  jwt: string,
  userNumericId: string,
  presetRelation: number | string,
): Promise<void> {
  const res = await fetch(`${STRAPI_URL}/api/users/${encodeURIComponent(userNumericId)}`, {
    method: 'PUT',
    headers: {
      ...strapiAuthHeaders(jwt),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ permission_preset_context: presetRelation }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
}

/**
 * Create or update this user’s single permission-preset and replace its many-to-many accesses.
 */
export async function strapiSaveUserPermissionPreset(
  jwt: string,
  userNumericId: string,
  permissionAccessDocumentIds: string[],
): Promise<void> {
  const user = await strapiFetchUserDetail(jwt, userNumericId);
  if (user.numericId == null) throw new Error('Invalid user id');

  if (user.permissionPresetDocumentId) {
    await strapiUpdatePermissionPresetAccesses(
      jwt,
      user.permissionPresetDocumentId,
      permissionAccessDocumentIds,
    );
    return;
  }

  const created = await strapiCreatePermissionPresetForUser(
    jwt,
    user.numericId,
    permissionAccessDocumentIds,
  );

  const after = await strapiFetchUserDetail(jwt, userNumericId);
  if (after.permissionPresetDocumentId) return;

  if (created.id != null) {
    await strapiLinkUserPermissionPreset(jwt, userNumericId, created.id);
    return;
  }
  if (created.documentId) {
    await strapiLinkUserPermissionPreset(jwt, userNumericId, created.documentId);
  }
}

/**
 * Resolves the Users & Permissions role with `type === "authenticated"` for `POST /api/users`.
 * Cached for the session to avoid an extra request on every create.
 */
let cachedAuthenticatedRoleId: number | null | undefined;

function roleRowType(raw: Record<string, unknown>): string {
  return String(flattenStrapiEntry(raw).type ?? raw.type ?? '');
}

function roleRowName(raw: Record<string, unknown>): string {
  return String(flattenStrapiEntry(raw).name ?? raw.name ?? '');
}

/** When Strapi ignores filters, the list can include Public + Authenticated; keep only the built-in Authenticated role. */
function pickAuthenticatedRole(rows: Record<string, unknown>[]): Record<string, unknown> | undefined {
  const byType = rows.filter((r) => roleRowType(r) === 'authenticated');
  if (byType.length === 1) return byType[0];
  if (byType.length > 1) {
    const exactName = byType.find((r) => roleRowName(r) === 'Authenticated');
    return exactName ?? byType.sort((a, b) => Number(a.id ?? 0) - Number(b.id ?? 0))[0];
  }
  if (rows.length === 1 && roleRowName(rows[0]) === 'Authenticated') {
    return rows[0];
  }
  return undefined;
}

async function fetchRoles(jwt: string, params: URLSearchParams): Promise<Record<string, unknown>[]> {
  const res = await fetch(`${STRAPI_URL}/api/users-permissions/roles?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  return parseListBody(res, body).rows;
}

export async function strapiResolveAuthenticatedRoleId(jwt: string): Promise<number> {
  if (cachedAuthenticatedRoleId != null) return cachedAuthenticatedRoleId;

  /** Tightest query first so the API returns a single row when Strapi applies filters. */
  const strict = new URLSearchParams({
    'filters[$and][0][type][$eq]': 'authenticated',
    'filters[$and][1][name][$eq]': 'Authenticated',
    'pagination[pageSize]': '1',
    'pagination[page]': '1',
  });

  const byName = new URLSearchParams({
    'filters[name][$eq]': 'Authenticated',
    'pagination[pageSize]': '1',
    'pagination[page]': '1',
  });

  const byType = new URLSearchParams({
    'filters[type][$eq]': 'authenticated',
    'pagination[pageSize]': '1',
    'pagination[page]': '1',
  });

  const fallback = new URLSearchParams({
    'pagination[pageSize]': '50',
    'pagination[page]': '1',
    sort: 'id:asc',
  });

  let authenticated: Record<string, unknown> | undefined;
  for (const params of [strict, byName, byType, fallback]) {
    const rows = await fetchRoles(jwt, params);
    authenticated = pickAuthenticatedRole(rows);
    if (authenticated) break;
  }

  if (!authenticated || roleRowType(authenticated) !== 'authenticated') {
    throw new Error('No Authenticated role found in Strapi.');
  }
  const flat = flattenStrapiEntry(authenticated);
  const rid = authenticated.id ?? flat.id;
  const n = rid != null ? Number(rid) : NaN;
  if (!Number.isFinite(n)) {
    throw new Error('Could not read Authenticated role id from Strapi.');
  }
  cachedAuthenticatedRoleId = n;
  return n;
}

/** Call after login change if you need to re-resolve roles (e.g. different Strapi env). */
export function clearAuthenticatedRoleIdCache(): void {
  cachedAuthenticatedRoleId = undefined;
}

/** POST /api/users/:id/impersonate — returns JWT for the target user (requires `can_view_user`). */
export async function strapiImpersonateUser(
  adminJwt: string,
  targetUserNumericId: string,
): Promise<AuthResponse> {
  const res = await fetch(
    `${STRAPI_URL}/api/users/${encodeURIComponent(targetUserNumericId)}/impersonate`,
    {
      method: 'POST',
      headers: strapiAuthHeaders(adminJwt),
    },
  );
  const data = (await res.json()) as AuthResponse & { error?: { message?: string } };
  if (!res.ok) {
    throw new Error(parseErrorMessage(data, res.status));
  }
  if (!data.jwt || !data.user) {
    throw new Error('Invalid impersonation response from Strapi');
  }
  const u = data.user as StrapiUser & Record<string, unknown>;
  return {
    jwt: data.jwt,
    user: {
      id: Number(u.id),
      username: String(u.username ?? ''),
      email: String(u.email ?? ''),
      blocked: u.blocked != null ? Boolean(u.blocked) : undefined,
      confirmed: u.confirmed != null ? Boolean(u.confirmed) : undefined,
    },
  };
}

export async function strapiDeleteManagedUser(jwt: string, userNumericId: string): Promise<void> {
  const res = await fetch(`${STRAPI_URL}/api/users/${encodeURIComponent(userNumericId)}`, {
    method: 'DELETE',
    headers: strapiAuthHeaders(jwt),
  });
  if (res.status === 204) return;
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
}

export async function strapiCreateManagedUser(
  jwt: string,
  input: {
    username: string;
    email: string;
    password: string;
    userTypeId: string;
    analisTypeDataIds?: string[];
  },
): Promise<void> {
  const roleId = await strapiResolveAuthenticatedRoleId(jwt);
  const payload: Record<string, unknown> = {
    username: input.username.trim(),
    email: input.email.trim(),
    password: input.password,
    blocked: false,
    confirmed: true,
    role: roleId,
    user_type: parseUserTypeRelationNumericId(input.userTypeId),
    analis_type_data: parseAnalisTypeDataIds(input.analisTypeDataIds ?? []),
  };
  const res = await fetch(`${STRAPI_URL}/api/users`, {
    method: 'POST',
    headers: {
      ...strapiAuthHeaders(jwt),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
}
